export const CREDIT_WEIGHTS = {
  promptTokens: 0.25,
  completionTokens: 1,
  textIn: 7,
  textOut: 27,
  audioIn: 54,
  audioOut: 107,
} as const;

export type CreditUsage = {
  promptTokens: number;
  completionTokens: number;
  textIn: number;
  textOut: number;
  audioIn: number;
  audioOut: number;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const sanitize = (value: unknown): number => (isFiniteNumber(value) ? value : 0);

const EMPTY_USAGE: CreditUsage = {
  promptTokens: 0,
  completionTokens: 0,
  textIn: 0,
  textOut: 0,
  audioIn: 0,
  audioOut: 0,
};

export const normalizeCreditUsage = (usage: Partial<CreditUsage> | undefined): CreditUsage => ({
  promptTokens: sanitize(usage?.promptTokens) || 0,
  completionTokens: sanitize(usage?.completionTokens) || 0,
  textIn: sanitize(usage?.textIn) || 0,
  textOut: sanitize(usage?.textOut) || 0,
  audioIn: sanitize(usage?.audioIn) || 0,
  audioOut: sanitize(usage?.audioOut) || 0,
});

export const createEmptyCreditUsage = (): CreditUsage => ({ ...EMPTY_USAGE });

export const calculateCredits = (usage: Partial<CreditUsage> | undefined): number => {
  const normalized = normalizeCreditUsage(usage);

  const weighted =
    normalized.promptTokens * CREDIT_WEIGHTS.promptTokens +
    normalized.completionTokens * CREDIT_WEIGHTS.completionTokens +
    normalized.textIn * CREDIT_WEIGHTS.textIn +
    normalized.textOut * CREDIT_WEIGHTS.textOut +
    normalized.audioIn * CREDIT_WEIGHTS.audioIn +
    normalized.audioOut * CREDIT_WEIGHTS.audioOut;

  return Math.max(0, weighted / 10000);
};


